import type { ReactNode } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { Badge } from '@savvy-kit/components/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import { CheckIcon, ChevronRightIcon, MinusIcon } from 'lucide-react';

import type { Column, FiltersState } from '../core/types';
import type { ExtraItem } from './filter-selector';

type FilterCategoryPanelProps<TData> = {
  readonly columns: Column<TData, 'option' | 'multiOption' | 'number'>[];
  readonly filters: FiltersState;
  readonly activeColumnId: string;
  readonly onSelect: (columnId: string) => void;
  readonly onActiveOffsetChange?: (panelOffsetTop: number) => void;
  readonly footer?: ReactNode;
  readonly extraItems?: ExtraItem[];
};

export function FilterCategoryPanel<TData>({
  columns,
  filters,
  activeColumnId,
  onSelect,
  onActiveOffsetChange,
  footer,
  extraItems,
}: FilterCategoryPanelProps<TData>) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const optionButtonRefs = useRef(new Map<string, HTMLButtonElement>());

  useLayoutEffect(() => {
    if (activeColumnId === '') {
      onActiveOffsetChange?.(0);
      return;
    }

    const panelElement = panelRef.current;
    const activeButton = optionButtonRefs.current.get(activeColumnId);
    if (isNil(panelElement) || isNil(activeButton)) {
      onActiveOffsetChange?.(0);
      return;
    }

    const panelTop = panelElement.getBoundingClientRect().top;
    const buttonTop = activeButton.getBoundingClientRect().top;
    const offsetTop = buttonTop - panelTop + panelElement.scrollTop;
    onActiveOffsetChange?.(offsetTop);
  }, [activeColumnId, onActiveOffsetChange]);

  return (
    <div ref={panelRef} className="flex flex-col p-1 min-w-[216px]">
      {columns.map((column) => {
        const isActive = column.id === activeColumnId;
        const filter = filters.find((f) => f.columnId === column.id);
        const hasIncludedValues = (filter?.values.length ?? 0) > 0;
        const hasExcludedValues = (filter?.excludedValues?.length ?? 0) > 0;
        const hasFilter = hasIncludedValues || hasExcludedValues;

        return (
          <button
            key={column.id}
            ref={(element) => {
              if (isNil(element)) {
                optionButtonRefs.current.delete(column.id);
                return;
              }
              optionButtonRefs.current.set(column.id, element);
            }}
            type="button"
            aria-label={column.displayName}
            className={cn(
              'flex items-center justify-between gap-2 p-2 text-sm text-left w-full rounded hover:bg-component-hover transition-colors',
              isActive && 'bg-component-hover font-semibold text-primary',
            )}
            onClick={() => onSelect(column.id)}
          >
            <span className="flex items-center gap-1.5">
              {column.displayName}
              {hasFilter && (
                <span data-testid={`category-dot-${column.id}`} className="inline-flex items-center gap-1 shrink-0">
                  {hasIncludedValues && (
                    <Badge
                      variant="primary-muted"
                      type="circle-sm"
                      data-testid={`category-dot-${column.id}-include`}
                      aria-label="Has included values"
                    >
                      <CheckIcon />
                    </Badge>
                  )}
                  {hasExcludedValues && (
                    <Badge
                      variant="destructive-muted"
                      type="circle-sm"
                      data-testid={`category-dot-${column.id}-exclude`}
                      aria-label="Has excluded values"
                    >
                      <MinusIcon />
                    </Badge>
                  )}
                </span>
              )}
            </span>
            <ChevronRightIcon className="size-4 text-muted-foreground shrink-0" />
          </button>
        );
      })}
      {(extraItems?.length || !isNil(footer)) && (
        <div className="border-t border-[var(--border-light)] mt-1 pt-1">
          {extraItems?.map((item) => (
            <Popover
              key={item.id}
              open={activeColumnId === item.id}
              onOpenChange={(open) => onSelect(open ? item.id : '')}
            >
              <PopoverTrigger
                render={(triggerProps) => (
                  <button
                    type="button"
                    className={cn(
                      'flex items-center justify-between gap-2 p-2 text-sm text-left w-full rounded hover:bg-component-hover transition-colors',
                      activeColumnId === item.id && 'bg-component-hover font-semibold text-primary',
                    )}
                    {...triggerProps}
                  >
                    <span className="flex items-center gap-1.5">
                      {item.icon}
                      {item.label}
                    </span>
                    <ChevronRightIcon className="size-4 text-muted-foreground shrink-0" />
                  </button>
                )}
              />
              <PopoverContent side="right" sideOffset={8} className="w-52 p-1">
                {item.content}
              </PopoverContent>
            </Popover>
          ))}
          {footer}
        </div>
      )}
    </div>
  );
}
